import React, { useState } from 'react';
import { useEventTrackerContext } from '../../contexts/EventTracker';
import { useSnackbarContext } from '../../contexts/Snackbar';
import { useLinesContext } from '../../contexts/Lines';
import { environments, applicationStatus } from '../../utils';
import {
  getContract,
  updateApplication,
  sendEmailContractSigned,
  approveRequestChange,
  finishWithRequest,
  addContractAddendum,
} from '../API';
import cloneDeep from 'lodash.clonedeep';
import messages from '../../messages';
import HelloSign from 'hellosign-embedded';
import Plan from './Plan';
import ContractAddendum from './ContractAddendum';

export default function PlanWrapper({
  plan,
  updatePlanName,
  expandRow,
  numColumns,
  openPlanDiscovery,
  deletePlan,
  rejectEditedPlan,
  plans,
  setPlans,
  setLoading,
}) {
  const snackbar = useSnackbarContext();
  const hellosign = new HelloSign();
  const { trackEvent } = useEventTrackerContext();
  const { setLines } = useLinesContext();

  const [openAddendumDialog, setOpenAddendumDialog] = useState(false);
  const [justSignedContract, setJustSignedContract] = useState(false);

  const originPlan = plans?.find((p) => p.id === plan.originApplication);

  hellosign.on('sign', async () => {
    try {
      if (!plan.originApplication) {
        await contractSigned();
      } else {
        setLoading(true);
        await Promise.all([contractSigned(), approveEditedPlan()]);
      }
    } catch (error) {
      console.log(`Error marking contract signed for plan ${plan?.id}`, error);
      snackbar.show(messages.plans.errors.acceptPlanChanges, 'error');
    }
    setLoading(false);
  });

  async function openContract(setPageLoading: boolean = true) {
    let isProduction = process.env.REACT_APP_ENV === environments.prod;
    try {
      setPageLoading && setLoading(true);
      const contract = await getContract(plan?.id);
      hellosign.open(contract.url, {
        clientId: 'be6db36930787b58990daa23b51454ba',
        skipDomainVerification: isProduction ? false : true,
      });
      trackEvent({ event: 'contract_opened' });
    } catch (error) {
      console.log('Error opening HelloSign dialog', error);
      let errorMessage = messages.plans.errors.openContract;
      if (error?.error === 'SIGNED_ALREADY') {
        errorMessage = error?.message;
      }
      if (error?.error === 'PLAN_OR_COMPANY_MISSING') {
        errorMessage = messages.plans.errors.missing;
      }
      snackbar.show(errorMessage, 'error');
    }
    setPageLoading && setLoading(false);
  }

  async function contractSigned() {
    // SET LOCALLY
    const id = plan.id;
    const plansCopy = cloneDeep(plans);
    const updatedPlans = plansCopy.map((plan) => {
      if (plan.id === id) {
        plan['contractSigned'] = true;
      }
      return plan;
    });
    setJustSignedContract(true);
    setPlans(updatedPlans);
    // SET IN DB
    await Promise.all([
      updateApplication({
        id,
        contractSigned: true,
      }),
      sendEmailContractSigned(id),
    ]);
    trackEvent({
      event: 'contract_signed',
      application_id: plan?.id,
      application_name: plan?.name,
      application_type: plan?.type,
      cost_of_capital: plan?.costOfCapital,
    });
  }

  async function openContractAddendum() {
    if (!originPlan?.contractVersion || originPlan?.contractVersion === 1) {
      await openContract();
    } else {
      setOpenAddendumDialog(true);
    }
  }

  async function approvedContractAddendum(
    text: string,
    linesInChangeRequest: Array<string>,
  ) {
    setLoading(true);
    try {
      await addContractAddendum(plan.id, text, linesInChangeRequest);
      await approveEditedPlan();
    } catch (error) {
      console.log(error);
      snackbar.show(messages.plans.errors.acceptPlanChanges, 'error');
    }
    setLoading(false);
  }

  async function approveEditedPlan() {
    setLines(null);

    const changeRequestId = plan.opcId;
    await approveRequestChange(changeRequestId);
    await finishWithRequest(changeRequestId);
    const plansCopy = cloneDeep(plans);
    const updatedPlans = plansCopy.map((p) => {
      if (p.id === plan.id) {
        p['status'] = applicationStatus.active.status;
      }
      if (p.id === plan.originApplication) {
        p['status'] = applicationStatus.archived.status;
      }
      return p;
    });
    setPlans(updatedPlans);
    snackbar.show(messages.plans.acceptPlanChanges, 'success');
    trackEvent({ event: 'plan_approved', plan });
  }

  return (
    <>
      <Plan
        plan={plan}
        updatePlanName={updatePlanName}
        expandRow={expandRow}
        numColumns={numColumns}
        openPlanDiscovery={openPlanDiscovery}
        deletePlan={deletePlan}
        openContract={openContract}
        openContractAddendum={openContractAddendum}
        rejectEditedPlan={rejectEditedPlan}
        justSignedContract={justSignedContract}
      />
      {/* Dialogs for Plan */}
      <ContractAddendum
        open={openAddendumDialog}
        handleClose={() => setOpenAddendumDialog(false)}
        linesInChangeRequest={plan?.linesInChangeRequest}
        loanTotal={plan?.total}
        loanRepayments={plan?.total + plan?.costOfCapital}
        planCreateDate={plan?.createdAt}
        originPlanCreateDate={originPlan?.createdAt}
        onSuccess={approvedContractAddendum}
      />
    </>
  );
}
